import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable, inject, Injector, signal, computed } from "@angular/core";
import { firstValueFrom, Observable, Subject } from "rxjs";
import {
    IDashboardModel,
    IDashboardApiResponse,
    IDashboardUpdateRequest,
    IDashboardUpdateResponse,
    IUserDashboardSettingsRequest,
} from "../../views-interfaces/dashboard-model.interface";
import { IUserDashboardSettingsModel } from "../../views-interfaces/user-dashboard-settings.interface";
import { AppSettingsService } from "../app-settings-service/app-settings.service";
import { AuthService } from "../auth";
import { LocalStorageService } from "../local-storage/local-storage.service";
import { ApplicationsService } from "../applications-service/applications.service";
import { ApplicationType } from "@app/views-interfaces/ApplicationType.enum";
import { ResponseErrorHandlerService } from "../response-error-handler-service/response-error-handler.service";

@Injectable()
export class DashboardsService {
    private readonly appSettingsService = inject(AppSettingsService);
    private readonly authService = inject(AuthService);
    private readonly http = inject(HttpClient);
    private readonly responseErrorHandlerService = inject(ResponseErrorHandlerService);
    private injector = inject(Injector);
    private firmId: string;

    private childForms = new Subject<string>();
    titleChangedEmitter = new Subject<string>();
    titleChangedEvent$ = this.titleChangedEmitter.asObservable();
    dashboardsLoadedEmitter = new Subject();
    dashboardsLoadedEvent$ = this.dashboardsLoadedEmitter.asObservable();
    dashboardsLoaded = false;
    private dashboardsLoadPromise: Promise<IDashboardModel[]>;

    Dashboards: IDashboardModel[] = [];
    private currentDashboardId: string;
    sDashboardId = signal<string>("");
    private accessibleDashboards: IDashboardModel[] = [];
    currentDashboarChangedEmitter = new Subject<string>();
    currentDashboardChanged$ = this.currentDashboarChangedEmitter.asObservable();
    dashboardChangeEmitter = new Subject<void>();
    dashboardChangeEvent$ = this.dashboardChangeEmitter.asObservable();

    sDashboards = signal<IDashboardModel[]>([]);

    readonly dashboardsById = computed(() => {
        const items = this.sDashboards();
        if (!items || !items.length) return;

        const dashboards = new Map<string, IDashboardModel>();
        for (const item of items) {
            dashboards.set(item.Id, item);
        }
        return dashboards;
    });

    public readonly currentDashboard = computed(() => {
        const dashboards = this.dashboardsById();
        if (!dashboards) return;
        return dashboards.get(this.sDashboardId());
    });

    public readonly isJobsBilling = computed<boolean>(() => {
        const currentDashboard = this.currentDashboard();
        return currentDashboard?.ApplicationType === ApplicationType.JobsBilling;
    });

    private readonly httpOptions = {
        headers: new HttpHeaders().set("Content-Type", "application/json"),
    };

    constructor() {
        this.authService.fetchedUserInfoEvent$.subscribe(() => {
            this.firmId = this.authService.currentUser.firm_id;
        });
    }

    refresh(forceReload = false) {
        this.dashboardsLoaded = false;
        this.Dashboards = [];
        this.sDashboards.set([]);
        if (forceReload || !this.dashboardsLoadPromise) {
            this.dashboardsLoadPromise = this.loadDashboards();
        }
    }

    async loadDashboards() {
        try {
            const result = await firstValueFrom(this.http.get<IDashboardApiResponse>(this.getUrl(), this.httpOptions));
            if (result.Code == 200) {
                this.Dashboards = result.Dashboards;
                this.sDashboards.set(result.Dashboards);
                this.dashboardsLoaded = true;
                this.dashboardsLoadedEmitter.next(true);
                return result.Dashboards;
            }
            this.responseErrorHandlerService.showPopup({
                typeLayout: "ERROR",
                messageCode: "defaultError",
            });
        } catch (error) {
            this.responseErrorHandlerService.showPopup({
                typeLayout: "ERROR",
                messageCode: "defaultError",
            });
        }
    }

    async getCurrentDashboard(allowDisabledUserDashboards = true) {
        return await this.getDashboard(await this.getSafeCurrentDashboardId(allowDisabledUserDashboards));
    }

    async getDashboard(dashboardId: string) {
        if (!this.dashboardsLoaded) {
            await this.getDashboardsList();
        }
        return this.Dashboards?.find((it) => it.Id === dashboardId);
    }

    async getDashboardsList() {
        if (!this.dashboardsLoadPromise) {
            this.dashboardsLoadPromise = this.loadDashboards();
        }
        return this.dashboardsLoadPromise;
    }

    updateDashboard(dashboardId: string | number, dashboardData: IDashboardUpdateRequest): Observable<IDashboardUpdateResponse> {
        return this.http.put<IDashboardUpdateResponse>(this.getUrl() + dashboardId, dashboardData, this.httpOptions);
    }

    private getUrl(path = "dashboard/") {
        return this.appSettingsService.appSettings.DashboardsService_URL + this.appSettingsService.appSettings.DashboardsService_Version + path;
    }

    saveAssignCategoryNoTags(dashboard: IDashboardModel, newValue: boolean, newCategoryId: string) {
        return this.http.put<IDashboardUpdateResponse>(
            this.getUrl() + dashboard.Id,
            { AssignCategoryNoTags: newValue, AssignCategoryId: newCategoryId },
            this.httpOptions,
        );
    }

    async preparingUDSForUserModel(UserDashboardSettings: IUserDashboardSettingsModel[]) {
        const preparedUDSModel: IUserDashboardSettingsRequest[] = [];
        (await this.getDashboardsList()).forEach((dashboard) => {
            let foundUDS: IUserDashboardSettingsModel | undefined = undefined;
            if (UserDashboardSettings?.find) {
                foundUDS = UserDashboardSettings.find((userDashboard) => {
                    return userDashboard.DashboardId === dashboard.Id;
                });
            }

            if (foundUDS?.Settings) {
                preparedUDSModel.push({
                    Active: foundUDS.Active,
                    Id: foundUDS.Id,
                    DashboardId: foundUDS.DashboardId,
                    UserId: foundUDS.UserId,
                    Title: dashboard.Title,
                    Settings: {
                        AutomaticSyncOpen: foundUDS.Settings.AutomaticSyncOpen,
                        AutomaticSyncClose: foundUDS.Settings.AutomaticSyncClose,
                    },
                });
            } else {
                // New user.
                preparedUDSModel.push({
                    Active: true,
                    DashboardId: dashboard.Id,
                    UserId: "",
                    Title: dashboard.Title,
                    Settings: {
                        AutomaticSyncOpen: false,
                        AutomaticSyncClose: false,
                    },
                });
            }
        });

        return preparedUDSModel;
    }

    // Service message commands
    triggerSubmitChildFormsEvent(event: string) {
        this.childForms.next(event);
    }

    triggerSaveCompleteChildFormsEvent(id: string) {
        this.childForms.next(id);
    }

    async UserDashboardSettingsModel() {
        return (await this.getDashboardsList()).map((dashboard) => {
            return {
                Active: true,
                DashboardId: dashboard.Id,
                UserId: "",
                Title: dashboard.Title,
                Settings: {
                    AutomaticSyncOpen: false,
                    AutomaticSyncClose: false,
                },
            };
        });
    }

    async getSafeCurrentDashboardId(allowDisabledUserDashboards = true) {
        if (!this.accessibleDashboards.length) await this.initAccessibleDashboards();
        let currentDashboardId = this.getCurrentDashboardId(allowDisabledUserDashboards);
        if (!currentDashboardId) {
            await this.getDashboardsList();
            currentDashboardId = this.getCurrentDashboardId(allowDisabledUserDashboards);
        }
        return currentDashboardId;
    }
    getCurrentDashboardId(allowDisabledUserDashboards = true) {
        const key = this.getCurrentDashboardIdKey();
        let saveDashboardId = false;
        let currentDashboardId = this.currentDashboardId;

        if (!currentDashboardId) {
            currentDashboardId = sessionStorage.getItem(key);
            if (!currentDashboardId) {
                saveDashboardId = true;
                currentDashboardId = localStorage.getItem(key);
            }
        }
        let dashboardIsInaccessible = !currentDashboardId;
        if (!dashboardIsInaccessible) {
            dashboardIsInaccessible = !this.Dashboards?.find((x) => x.Id === currentDashboardId);
            if (!dashboardIsInaccessible && !allowDisabledUserDashboards) {
                dashboardIsInaccessible = !this.accessibleDashboards.find((x) => x.Id === currentDashboardId);
            }
        }
        if (dashboardIsInaccessible) {
            saveDashboardId = true;
            currentDashboardId = this.getFirstAccessibleDashboardId();
        }
        if (currentDashboardId && saveDashboardId) {
            sessionStorage.setItem(key, currentDashboardId);
            LocalStorageService.setItem(key, currentDashboardId);
        }
        this.currentDashboardId = currentDashboardId;
        this.sDashboardId.set(this.currentDashboardId);
        return this.currentDashboardId;
    }

    private getFirstAccessibleDashboardId() {
        if (this.accessibleDashboards && this.accessibleDashboards.length > 0) {
            return this.accessibleDashboards.sort((a, b) => b.TaxYear - a.TaxYear)[0].Id;
        } else {
            return undefined;
        }
    }

    setCurrentDashboardId(dashboardId: string) {
        const key = this.getCurrentDashboardIdKey();
        this.currentDashboardId = dashboardId;
        this.sDashboardId.set(this.currentDashboardId);
        sessionStorage.setItem(key, this.currentDashboardId);
        LocalStorageService.setItem(key, this.currentDashboardId);
        this.currentDashboarChangedEmitter.next(this.currentDashboardId);
    }

    getCurrentDashboardIdKey() {
        return "currentDashboard_" + this.firmId;
    }

    async initAccessibleDashboards() {
        const applicationsService = this.injector.get(ApplicationsService);
        this.accessibleDashboards = await applicationsService.getAccessibleDashboards();
    }

    getDashboardYear(dashboard: IDashboardModel): number | null {
        if (dashboard.ApplicationType === ApplicationType.JobsBilling) {
            return null;
        }
        if (dashboard.TaxYear) {
            return dashboard.TaxYear;
        }
        return Number.parseInt(dashboard.DatabaseName.substring(9));
    }
}
